import { useMemo } from 'react';

import {
  Box,
  type BoxProps,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Alert,
  AlertTitle,
  AlertIcon,
} from '@frontend/components';

import { SUPPLIER_ROLES, PERMISSIONS } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';
import {
  getActiveGroup,
  getGroupMemberSupplierRoles,
  getUserGroupCollectionId,
} from 'store/session/selectors';

import NoResults from './components/NoResults';
import Searching from './components/Searching';
import { TABS as TAB_TYPES } from './constants';
import { useSearchDataContext } from './context';
import OrderItemResultList from './orderItems/OrderItemResultList';
import OrderResultList from './orders/OrderResultList';
import PartsResultList from './parts/PartsResultList';
import QuoteItemResultList from './quoteItems/QuoteItemResultList';
import QuoteResultList from './quotes/QuoteResultList';
import ShareResultList from './shares/ShareResultList';

const Container = ({ children, ...rest }: BoxProps) => (
  <Box
    bg="page.surface"
    borderBottomLeftRadius="5px"
    borderBottomRightRadius="5px"
    boxShadow="popover"
    display="flex"
    maxHeight={{ base: 'calc(100vh - 48px)', tablet: 'calc(100vh - 120px)' }}
    overflow="hidden"
    position="absolute"
    top="32px"
    width="100%"
    zIndex={1000}
    border="1px solid"
    borderColor="separator.border"
    borderTop="none"
    {...rest}
  >
    {children}
  </Box>
);

export type SearchResultsProps = BoxProps;

const SearchResults = (props: BoxProps) => {
  const roles = useAppSelector(getGroupMemberSupplierRoles);
  const isQuotingAvailable = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );
  const areQuotesAvailable =
    isQuotingAvailable &&
    roles[SUPPLIER_ROLES.QUOTES] !== PERMISSIONS.NO_ACCESS;
  const areOrdersAvailable =
    areQuotesAvailable &&
    roles[SUPPLIER_ROLES.ORDERS] !== PERMISSIONS.NO_ACCESS;

  const {
    data: { parts, shares, quotes, quoteItems, orders, orderItems },
    searchTerm,
    setActiveTab,
    activeTab,
  } = useSearchDataContext();
  const tabTypesArray = [
    TAB_TYPES.ALL,
    ...(areQuotesAvailable ? [TAB_TYPES.QUOTE_ITEMS, TAB_TYPES.QUOTES] : []),
    ...(areOrdersAvailable ? [TAB_TYPES.ORDER_ITEMS, TAB_TYPES.ORDERS] : []),
    TAB_TYPES.PARTS,
    TAB_TYPES.SHARES,
  ];

  const partCount = parts?.count;
  const shareCount = shares?.count;
  const quoteCount = quotes?.count;
  const quoteItemCount = quoteItems?.count;
  const orderCount = orders?.count;
  const orderItemCount = orderItems?.count;

  const isSearching = useMemo(() => {
    const quotesIsLoading = areQuotesAvailable && (!quotes || !quoteItems);
    const ordersIsLoading = areOrdersAvailable && (!orders || !orderItems);
    return !parts || !shares || quotesIsLoading || ordersIsLoading;
  }, [
    areQuotesAvailable,
    areOrdersAvailable,
    parts,
    shares,
    quotes,
    quoteItems,
    orders,
    orderItems,
  ]);

  const hasResults =
    partCount ||
    shareCount ||
    orderCount ||
    orderItemCount ||
    quoteCount ||
    quoteItemCount;

  const isMultisiteEnabled = useFeatureFlagValue(
    featureFlagKeys.isMultisiteFunctionalityAvailable,
  );
  const userGroupCollectionId = useAppSelector(getUserGroupCollectionId);
  const activeGroup = useAppSelector(getActiveGroup);

  return (
    <Container {...props}>
      {isSearching ? (
        <Searching />
      ) : hasResults ? (
        <>
          <Tabs
            width="100%"
            index={tabTypesArray.indexOf(activeTab)}
            onChange={(idx) => {
              const tabKey = tabTypesArray[idx];
              if (activeTab !== tabKey) {
                setActiveTab(tabKey);
              }
            }}
          >
            <TabList flexShrink={0}>
              <Tab>Top Results</Tab>
              {areQuotesAvailable && (
                <>
                  <Tab>{`Quote Items (${quoteItemCount})`}</Tab>
                  <Tab>{`Quotes (${quoteCount})`}</Tab>
                </>
              )}
              {areOrdersAvailable && (
                <>
                  <Tab>{`Order Items (${orderItemCount})`}</Tab>
                  <Tab>{`Orders (${orderCount})`}</Tab>
                </>
              )}
              <Tab>{`Parts (${partCount})`}</Tab>
              <Tab>{`Shared with Me (${shareCount})`}</Tab>
            </TabList>
            <TabPanels overflow="auto">
              <TabPanel p={0} height="100%" tabIndex={-1}>
                <Box overflow="auto" height="100%">
                  {areQuotesAvailable && (
                    <>
                      <QuoteItemResultList />
                      <QuoteResultList />
                    </>
                  )}
                  {areOrdersAvailable && (
                    <>
                      <OrderItemResultList />
                      <OrderResultList />
                    </>
                  )}
                  <PartsResultList />
                  <ShareResultList />
                </Box>
              </TabPanel>
              {areQuotesAvailable && (
                <TabPanel p={0} height="100%">
                  <QuoteItemResultList key={searchTerm} />
                </TabPanel>
              )}
              {areQuotesAvailable && (
                <TabPanel p={0} height="100%">
                  <QuoteResultList key={searchTerm} />
                </TabPanel>
              )}
              {areOrdersAvailable && (
                <TabPanel p={0} height="100%">
                  <OrderItemResultList key={searchTerm} />
                </TabPanel>
              )}
              {areOrdersAvailable && (
                <TabPanel p={0} height="100%">
                  <OrderResultList key={searchTerm} />
                </TabPanel>
              )}
              <TabPanel p={0} height="100%">
                <PartsResultList key={searchTerm} />
              </TabPanel>
              <TabPanel p={0}>
                {isMultisiteEnabled &&
                  !!userGroupCollectionId &&
                  !!activeGroup && (
                    <Alert status="warning" variant="borderless" size="sm">
                      <AlertIcon />
                      <AlertTitle>
                        These results are limited to your current active site,{' '}
                        {activeGroup.businessName}
                      </AlertTitle>
                    </Alert>
                  )}
                <ShareResultList key={searchTerm} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      ) : (
        <NoResults />
      )}
    </Container>
  );
};

export default SearchResults;
